// Browser recovery intent only: no HTML, assets, policy or credentials. The DB
// admission remains authoritative. Storage failure must precede the first POST.
import { espOperationValid } from './review-esp.js';
const SHA = /^[a-f0-9]{64}$/, KEY = /^[A-Za-z0-9_-]{16,128}$/;
const slot = (context, job) => 'goose:esp-intent:v1:' + encodeURIComponent(JSON.stringify([context, job]));
export function validEspBody(body) {
  return body && ['esp,esp_tier,revision,revision_digest','esp,esp_tier,policy_input_digest,revision,revision_digest'].includes(Object.keys(body).sort().join(','))
    && (!Object.hasOwn(body,'policy_input_digest')||SHA.test(body.policy_input_digest||''))
    && /^r(?:0|[1-9][0-9]*)$/.test(body.revision || '') && SHA.test(body.revision_digest || '')
    && (body.esp === 'sfmc' && ['basic','advanced'].includes(body.esp_tier) || body.esp === 'hubspot' && body.esp_tier === null);
}
const fingerprint = body => JSON.stringify([body.revision, body.revision_digest, body.esp, body.esp_tier,...(body.policy_input_digest?[body.policy_input_digest]:[])]);
export function listEspIntents(context, job, storage = globalThis.localStorage) {
  if (!context || !/^[A-Za-z0-9_-]{1,128}$/.test(job || '')) throw Error('Reload the build before recovering an ESP request.');
  let rows; try { rows = JSON.parse(storage.getItem(slot(context, job)) || '[]'); } catch { throw Error('ESP request history is unreadable. Do not resubmit until recovered.'); }
  if (!Array.isArray(rows) || rows.length > 200 || rows.some(r => !r || !KEY.test(r.key || '') || !validEspBody(r.body)
    || typeof r.retired !== 'boolean' || !['unconfirmed','prepared','converted','needs_review'].includes(r.state))) throw Error('Saved ESP request history is invalid.');
  return rows;
}
function save(context, job, rows, storage) {
  if (rows.length > 200) throw Error('This browser has 200 retained ESP intents. Recover them before creating more.');
  const raw = JSON.stringify(rows); storage.setItem(slot(context, job), raw);
  if (storage.getItem(slot(context, job)) !== raw) throw Error('Cannot safely retain the ESP request. Enable browser storage before retrying.');
}
function locked(context, job, locks, action) {
  if (!locks?.request) throw Error('Safe cross-tab ESP recovery requires browser locking. Use a supported secure browser before creating a request.');
  return locks.request(slot(context, job), { mode: 'exclusive' }, action);
}
export async function retainEspIntent(context, job, body, { storage = globalThis.localStorage, crypto = globalThis.crypto, locks = globalThis.navigator?.locks } = {}) {
  if (!validEspBody(body)) throw Error('ESP conversion requires an exact revision and supported target.');
  return locked(context, job, locks, () => {
  const rows = listEspIntents(context, job, storage), old = rows.find(r => !r.retired && fingerprint(r.body) === fingerprint(body));
  if (old) return old;
  const row = { key: crypto.randomUUID(), body: { ...body }, retired: false, state: 'unconfirmed' };
  save(context, job, [...rows, row], storage); return row;
  });
}
export async function recordEspIntent(context, job, key, result, storage = globalThis.localStorage, locks = globalThis.navigator?.locks) {
  if (result?.schema === 'EspConversionStatus.v1' && result.state === 'not_recorded' && result.found === false && result.deliverable === false) return;
  return locked(context, job, locks, () => {
  const rows = listEspIntents(context, job, storage), saved = rows.find(r => r.key === key);
  if (!saved || !espOperationValid(result, { jobId: job, expected: { ...saved.body, operation_id: saved.operation_id, output_digest: saved.output_digest } })) throw Error('ESP response does not match the retained request.');
  // A delayed admission response from another tab must not erase a terminal
  // observation. Server admissions/results are immutable, not last-write-wins.
  if (['converted','needs_review'].includes(saved.state)) {
    if (result.state === 'prepared') return;
    if (result.state !== saved.state) throw Error('ESP terminal response changed. Recover server history.');
  }
  save(context, job, rows.map(r => r.key === key ? { ...r, state: result.state, operation_id: result.operation_id, output_digest: result.output_digest } : r), storage);
  });
}
export async function retireEspIntent(context, job, key, storage = globalThis.localStorage, locks = globalThis.navigator?.locks) {
  return locked(context, job, locks, () => {
  const rows = listEspIntents(context, job, storage), row = rows.find(r => r.key === key);
  if (!row || !['converted','needs_review'].includes(row.state)) throw Error('Recover the unfinished ESP request before creating another.');
  save(context, job, rows.map(r => r.key === key ? { ...r, retired: true } : r), storage);
  });
}
